import type { Cart } from '@tix/entity'
import type { Schema } from '@tix/openapi-sdk'

import { type DonationOptionsValues, donationOptionsFromCart } from '@/helpers/donationSurvey'
import { getEnv } from '@/helpers/environment'
import { triggerGA4Event } from '@/helpers/ga4'

/**
 * Donation ecommerce item — mirrors web-client `ItemPayload` fields used for tickets.
 *
 * @see apps/web-client/src/types/ga4.ts
 * @see apps/web-client/src/checkout/helpers/ga4.ts
 */
export interface GA4DonationItem {
  item_id: string
  item_name: string
  item_category: string
  item_category2: string
  item_category3: string
  item_category4: string
  price: number
  coupon: string
  quantity: number
  discount: number
  donation_visibility?: 'anonymous' | 'public'
  donation_type?: 'dedication' | 'standard'
}

/** view_item only needs event-level fields (web-client `getEventPayload`). */
interface GA4DonationViewItem {
  item_id: string
  item_name: string
  item_category: string
}

export interface GA4DonationViewPayload {
  currency: string
  value: number
  items: GA4DonationViewItem[]
}

export interface GA4DonationEcommercePayload {
  currency: string
  value: number
  items: GA4DonationItem[]
}

export interface GA4DonationPurchasePayload extends GA4DonationEcommercePayload {
  transaction_id: string
  coupon: string
}

export type DonationEventTemplate = Pick<Schema<'event_template'>, 'id' | 'name' | 'category'>
export type DonationTicketGroup = Pick<Schema<'ticket_group'>, 'id' | 'name' | 'handler'>
export type DonationTicketType = Pick<Schema<'ticket_type'>, 'id' | 'name'>
type DonationTicket = Pick<Schema<'ticket'>, 'ticket_type_id' | 'face_value' | 'adjusted_value' | 'currency_code'>

export type DonationGa4Context = {
  eventTemplate: DonationEventTemplate
  donationGroup: DonationTicketGroup
  ticketTypes: DonationTicketType[]
}

function donationVisibility(options: DonationOptionsValues): 'anonymous' | 'public' {
  return options.anonymous ? 'anonymous' : 'public'
}

function donationType(options: DonationOptionsValues): 'dedication' | 'standard' {
  return options.dedicate ? 'dedication' : 'standard'
}

function ticketTypeName(ticketTypes: DonationTicketType[], ticketTypeId: string): string {
  return ticketTypes.find((type) => type.id === ticketTypeId)?.name ?? ''
}

function itemPrice(ticket: DonationTicket): { price: number; discount: number } {
  const face = Number(ticket.face_value)
  const adjusted = ticket.adjusted_value != null ? Number(ticket.adjusted_value) : face
  const price = Number.isFinite(adjusted) ? adjusted : 0
  const discount = Number.isFinite(face) ? face - price : 0
  return { price, discount }
}

/** view_item item — event-level only (matches web-client `getEventPayload`). */
function buildDonationViewItem(ctx: DonationGa4Context): GA4DonationViewItem {
  return {
    item_id: ctx.eventTemplate.id,
    item_name: ctx.eventTemplate.name ?? '',
    item_category: ctx.eventTemplate.category ?? '',
  }
}

export function buildDonationViewPayload(ctx: DonationGa4Context, currency: string): GA4DonationViewPayload {
  return {
    currency,
    value: 0,
    items: [buildDonationViewItem(ctx)],
  }
}

/** Cart / checkout item fields shared by add_to_cart, begin_checkout, and purchase. */
function buildDonationCartItem(
  ctx: DonationGa4Context,
  ticket: DonationTicket,
  options?: DonationOptionsValues,
): GA4DonationItem {
  const { price, discount } = itemPrice(ticket)
  const item: GA4DonationItem = {
    item_id: ctx.eventTemplate.id,
    item_name: ctx.eventTemplate.name ?? '',
    item_category: ctx.eventTemplate.category ?? '',
    item_category2: ctx.donationGroup.name ?? '',
    item_category3: ticketTypeName(ctx.ticketTypes, ticket.ticket_type_id),
    item_category4: ctx.donationGroup.handler,
    price,
    coupon: '',
    quantity: 1,
    discount,
  }

  if (options) {
    item.donation_visibility = donationVisibility(options)
    item.donation_type = donationType(options)
  }

  return item
}

export function buildDonationCartPayload(
  ctx: DonationGa4Context,
  ticket: DonationTicket,
  currency: string,
): GA4DonationEcommercePayload {
  const item = buildDonationCartItem(ctx, ticket)
  return {
    currency,
    value: item.price * item.quantity,
    items: [item],
  }
}

function buildDonationPurchasePayload(
  ctx: DonationGa4Context,
  ticket: DonationTicket,
  options: DonationOptionsValues,
  currency: string,
  transactionId: string,
): GA4DonationPurchasePayload {
  const item = buildDonationCartItem(ctx, ticket, options)
  return {
    currency,
    transaction_id: transactionId,
    value: item.price * item.quantity,
    coupon: '',
    items: [item],
  }
}

export function trackDonationPurchase(
  ctx: DonationGa4Context,
  ticket: DonationTicket,
  options: DonationOptionsValues,
  currency: string,
  transactionId: string,
): void {
  triggerGA4Event('purchase', buildDonationPurchasePayload(ctx, ticket, options, currency, transactionId))
}

/** True when the cart’s first ticket belongs to a ticket group with `handler: 'donation'`. */
export function isDonationCart(cart: Cart): boolean {
  const ticket = cart.tickets?.[0]
  if (!ticket) return false
  return cart.ticketGroups?.find((group) => group.id === ticket.ticket_group_id)?.handler === 'donation'
}

/**
 * Rebuilds purchase from a settled donation cart (redirect payments). Requires the cart embeds in {@link DONATION_PURCHASE_CART_EMBEDS}. Call only when {@link isDonationCart} is true; returns `null` if embeds are incomplete.
 */
function buildDonationPurchaseFromCart(cart: Cart, transactionId: string): GA4DonationPurchasePayload | null {
  const ticket = cart.tickets?.[0]
  if (!ticket) return null

  const donationGroup = cart.ticketGroups?.find((group) => group.id === ticket.ticket_group_id)
  if (!donationGroup) return null

  const eventTemplate = cart.eventTemplates?.find((template) => template.id === ticket.event_template_id)
  if (!eventTemplate) return null

  return buildDonationPurchasePayload(
    { eventTemplate, donationGroup, ticketTypes: cart.ticketTypes ?? [] },
    ticket,
    donationOptionsFromCart(cart.data?.additional_info, ticket.ticket_group_id),
    ticket.currency_code || getEnv().portal.default_currency_code,
    transactionId,
  )
}

/** Cart embeds needed to name the event, ticket group and ticket type when rebuilding purchase from a settled cart. */
export const DONATION_PURCHASE_CART_EMBEDS = [
  'ticket',
  'ticket.ticket_type',
  'ticket.ticket_group',
  'ticket.event_template',
] as const

/**
 * Fire a donation `purchase` derived from a settled donation cart. Call only when {@link isDonationCart} is true.
 */
export function trackDonationPurchaseFromCart(cart: Cart, transactionId: string): void {
  const payload = buildDonationPurchaseFromCart(cart, transactionId)
  if (payload) triggerGA4Event('purchase', payload)
}
